import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../../../users/adminUserFormatters";
import {
  boundsToMapboxCoordinates,
  CALIBRATION_QA_CHECKLIST,
  COLOMBIA_CALIBRATION_REFERENCE_POINTS,
  nudgeBounds,
  NUDGE_STEPS,
  validateBounds,
} from "../../../../../../utils/ideamBartBoundsUtils";
import {
  activateRadarCalibration,
  buildCalibrationDiagnostics,
  resetPreviewBounds,
  resolveEffectiveCalibrationBounds,
} from "../../../../../../utils/radarCalibrationStore";
import { fetchBartCalibration } from "../../../../../../weather/adminRadarActionsApi";
import { IDEAM_BART_CALIBRATION_CONFIG } from "../../../../../../constants/ideamBartRadarOverlay";
import { formatFrameAge } from "../../../../../../utils/radarHealthAdminUtils";

const OPACITY_PRESETS = [0.3, 0.5, 0.7, 0.9];

function BoundsField({ label, value, onChange, error }) {
  return (
    <label className="weather-radar-calibration__field">
      <span>{label}</span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {error ? <span className="weather-radar-calibration__field-error">{error}</span> : null}
    </label>
  );
}

const WeatherRadarCalibrationPanel = ({ calibrationState, onUpdateState, canManage = false }) => {
  const [serverCalibration, setServerCalibration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [boundsError, setBoundsError] = useState("");
  const [toast, setToast] = useState("");

  const previewBounds = calibrationState.previewBounds;
  const validation = useMemo(() => validateBounds(previewBounds), [previewBounds]);

  const loadCalibration = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const payload = await fetchBartCalibration();
      setServerCalibration(payload);
    } catch (error) {
      setServerCalibration(null);
      setErrorMessage(error.message || "Calibration endpoint unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalibration();
  }, [loadCalibration]);

  const applyBounds = (nextBounds) => {
    const result = validateBounds(nextBounds);
    if (!result.valid) {
      setBoundsError(result.message);
      return;
    }

    setBoundsError("");
    onUpdateState({ previewBounds: nextBounds });
  };

  const handleNudge = (direction) => {
    applyBounds(nudgeBounds(previewBounds, direction, calibrationState.nudgeStep));
  };

  const handleCopy = async (payload, successMessage) => {
    try {
      await navigator.clipboard.writeText(
        typeof payload === "string" ? payload : JSON.stringify(payload, null, 2)
      );
      setToast(successMessage);
    } catch {
      window.prompt("Copy calibration output:", JSON.stringify(payload, null, 2));
    }
  };

  const handleCopyBoundsJson = () => {
    handleCopy(
      {
        bounds: previewBounds,
        mapboxCoordinates: boundsToMapboxCoordinates(previewBounds),
        calibrationVersion: calibrationState.calibrationVersion,
        notes: calibrationState.notes,
      },
      "Bounds JSON copied."
    );
  };

  const handleCopyMapboxCoordinates = () => {
    handleCopy(boundsToMapboxCoordinates(previewBounds), "Mapbox coordinates copied.");
  };

  const handleCopyDiagnostics = () => {
    handleCopy(
      {
        ...buildCalibrationDiagnostics(calibrationState),
        latestFrameTime: serverCalibration?.latestFrameTime || null,
        latestFrameUrl: serverCalibration?.latestFrameUrl || null,
        latestFrameAgeMinutes: serverCalibration?.latestFrameAgeMinutes ?? null,
        configuredBounds: calibrationState.configuredBounds,
      },
      "Calibration diagnostics copied."
    );
  };

  const handleReset = () => {
    resetPreviewBounds();
    setBoundsError("");
    setToast("Reset to configured bounds.");
  };

  const handleOpenMapPreview = () => {
    activateRadarCalibration({
      showBoundsOutline: calibrationState.showBoundsOutline,
      showReferenceMarkers: calibrationState.showReferenceMarkers,
      comparisonMode: calibrationState.comparisonMode,
      opacity: calibrationState.opacity,
    });
    window.location.assign("/?perspective=weather&radarCalibration=1");
  };

  if (!canManage) {
    return (
      <section id="radar-calibration" className="weather-radar-calibration panel-card">
        <h2 className="admin-section-title">Weather Radar Calibration</h2>
        <p className="weather-radar-calibration__notice">
          Radar calibration tools require weather layer manage permission.
        </p>
      </section>
    );
  }

  return (
    <section id="radar-calibration" className="weather-radar-calibration panel-card">
      <div className="weather-radar-calibration__header">
        <div>
          <h2 className="admin-section-title">Weather Radar Calibration</h2>
          <p className="weather-radar-calibration__description">
            Visual QA for IDEAM BART georeference alignment. Temporary preview changes are not
            persisted automatically.
          </p>
        </div>
        <button type="button" className="admin-dashboard__action-btn" onClick={loadCalibration}>
          Refresh
        </button>
      </div>

      {toast ? (
        <p className="weather-radar-calibration__toast" role="status">
          {toast}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading calibration diagnostics…</p> : null}
      {errorMessage ? <p className="admin-re-error">{errorMessage}</p> : null}

      <div className="weather-radar-calibration__summary">
        <div>
          <span className="weather-radar-calibration__label">Source</span>
          <strong>{IDEAM_BART_CALIBRATION_CONFIG.sourceLabel}</strong>
          <span className="weather-radar-calibration__badge weather-radar-calibration__badge--official">
            Official
          </span>
        </div>
        <div>
          <span className="weather-radar-calibration__label">Calibration version</span>
          <span>{calibrationState.calibrationVersion}</span>
        </div>
        <div>
          <span className="weather-radar-calibration__label">Latest frame</span>
          <span>
            {serverCalibration?.latestFrameTime
              ? formatDateTime(serverCalibration.latestFrameTime)
              : "—"}
          </span>
        </div>
        <div>
          <span className="weather-radar-calibration__label">Frame age</span>
          <span>{formatFrameAge(serverCalibration?.latestFrameAgeMinutes)}</span>
        </div>
      </div>

      <div className="weather-radar-calibration__toggles">
        <label>
          <input
            type="checkbox"
            checked={calibrationState.showBoundsOutline}
            onChange={(event) => onUpdateState({ showBoundsOutline: event.target.checked })}
          />
          Show BART bounds outline
        </label>
        <label>
          <input
            type="checkbox"
            checked={calibrationState.showReferenceMarkers}
            onChange={(event) => onUpdateState({ showReferenceMarkers: event.target.checked })}
          />
          Show reference markers
        </label>
        <label>
          <input
            type="checkbox"
            checked={calibrationState.active}
            onChange={(event) => onUpdateState({ active: event.target.checked })}
          />
          Calibration preview active
        </label>
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Comparison mode</h3>
        <p className="muted">Admin/debug only. RainViewer is always labeled Backup.</p>
        <div className="weather-radar-calibration__comparison">
          {[
            { value: "official", label: "Official (BART only)" },
            { value: "backup", label: "Backup (RainViewer only)" },
            { value: "both", label: "Both (adjustable opacity)" },
            { value: "configured", label: "Use configured source mode" },
          ].map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name="comparisonMode"
                value={option.value}
                checked={calibrationState.comparisonMode === option.value}
                onChange={() => onUpdateState({ comparisonMode: option.value })}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Opacity</h3>
        <label className="weather-radar-calibration__slider">
          <span>BART opacity ({Math.round(calibrationState.opacity * 100)}%)</span>
          <input
            type="range"
            min="0.1"
            max="1"
            step="0.05"
            value={calibrationState.opacity}
            onChange={(event) => onUpdateState({ opacity: Number(event.target.value) })}
          />
        </label>
        <div className="weather-radar-calibration__presets">
          {OPACITY_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
              onClick={() => onUpdateState({ opacity: preset })}
            >
              {Math.round(preset * 100)}%
            </button>
          ))}
        </div>
        {calibrationState.comparisonMode === "both" ? (
          <label className="weather-radar-calibration__slider">
            <span>
              RainViewer backup opacity ({Math.round(calibrationState.backupOpacity * 100)}%)
            </span>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.05"
              value={calibrationState.backupOpacity}
              onChange={(event) => onUpdateState({ backupOpacity: Number(event.target.value) })}
            />
          </label>
        ) : null}
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Bounds adjustment (preview only)</h3>
        <div className="weather-radar-calibration__bounds-grid">
          <BoundsField
            label="westLng"
            value={previewBounds.westLng}
            onChange={(value) => applyBounds({ ...previewBounds, westLng: value })}
          />
          <BoundsField
            label="southLat"
            value={previewBounds.southLat}
            onChange={(value) => applyBounds({ ...previewBounds, southLat: value })}
          />
          <BoundsField
            label="eastLng"
            value={previewBounds.eastLng}
            onChange={(value) => applyBounds({ ...previewBounds, eastLng: value })}
          />
          <BoundsField
            label="northLat"
            value={previewBounds.northLat}
            onChange={(value) => applyBounds({ ...previewBounds, northLat: value })}
          />
        </div>
        {boundsError || !validation.valid ? (
          <p className="admin-re-error">{boundsError || validation.message}</p>
        ) : null}

        <label className="weather-radar-calibration__field">
          <span>Nudge step (degrees)</span>
          <select
            value={calibrationState.nudgeStep}
            onChange={(event) => onUpdateState({ nudgeStep: Number(event.target.value) })}
          >
            {NUDGE_STEPS.map((step) => (
              <option key={step} value={step}>
                {step}
              </option>
            ))}
          </select>
        </label>

        <div className="weather-radar-calibration__nudge-grid">
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("west")}>
            ← West
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("east")}>
            East →
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("north")}>
            ↑ North
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("south")}>
            ↓ South
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("expandX")}>
            Expand ↔
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("contractX")}>
            Contract ↔
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("expandY")}>
            Expand ↕
          </button>
          <button type="button" className="admin-dashboard__action-btn" onClick={() => handleNudge("contractY")}>
            Contract ↕
          </button>
        </div>
      </div>

      <div className="weather-radar-calibration__actions">
        <button type="button" className="admin-dashboard__action-btn" onClick={handleReset}>
          Reset to configured bounds
        </button>
        <button type="button" className="admin-dashboard__action-btn" onClick={handleCopyBoundsJson}>
          Copy bounds JSON
        </button>
        <button type="button" className="admin-dashboard__action-btn" onClick={handleCopyMapboxCoordinates}>
          Copy Mapbox coordinates
        </button>
        <button type="button" className="admin-dashboard__action-btn" onClick={handleCopyDiagnostics}>
          Copy calibration diagnostics
        </button>
        <button type="button" className="admin-dashboard__action-btn" onClick={handleOpenMapPreview}>
          Open map for visual QA
        </button>
        <Link className="admin-dashboard__action-btn" to="/admin/weather/sources#radar-health">
          Back to radar health
        </Link>
      </div>

      <p className="weather-radar-calibration__persist-note">
        TODO: Persisting calibration bounds should be handled by future admin settings work. Use
        copied JSON to update <code>REACT_APP_IDEAM_BART_BOUNDS</code> or centralized config after
        visual validation.
      </p>

      <div className="weather-radar-calibration__section">
        <h3>Reference points</h3>
        <ul className="weather-radar-calibration__references">
          {COLOMBIA_CALIBRATION_REFERENCE_POINTS.map((point) => (
            <li key={point.id}>
              <strong>{point.name}</strong> — {point.lng}, {point.lat}
              <span className="muted"> ({point.purpose})</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Visual QA checklist</h3>
        <ul className="weather-radar-calibration__checklist">
          {CALIBRATION_QA_CHECKLIST.map((item) => (
            <li key={item}>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(calibrationState.checklist?.[item])}
                  onChange={(event) =>
                    onUpdateState({
                      checklist: {
                        ...calibrationState.checklist,
                        [item]: event.target.checked,
                      },
                    })
                  }
                />
                {item}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <details className="weather-radar-calibration__details">
        <summary>Current preview bounds</summary>
        <pre>{JSON.stringify(resolveEffectiveCalibrationBounds(calibrationState), null, 2)}</pre>
      </details>
    </section>
  );
};

export default WeatherRadarCalibrationPanel;
